/* eslint-disable check-file/filename-naming-convention */
import { ComponentProps } from 'react'

import { parseWithZod } from '@conform-to/zod'
import {
  ActionFunctionArgs,
  json,
  LoaderFunction,
  MetaFunction,
  redirect,
} from '@remix-run/node'
import { useLoaderData, useSearchParams } from '@remix-run/react'
import { z } from 'zod'

import { Breadcrumb, Container } from '~/components'
import { NO_IMAGE_URL } from '~/constants'
import {
  isSearchResultOrderType,
  partialNumberOfUsersMultiSelectFormSchema,
  partialFacilitiesSchemaSchema,
  SEARCH_RESULT_ORDERS,
  SearchResultOrder,
  SearchResultPage,
  searchFormSchema,
  partialMonthlyCostsFormSchema,
} from '~/features'
import {
  partialAreasMultiSelectFormSchema,
  partialKeywordsSchema,
  partialOfficeTypeMultiSelectFormSchema,
  PrefectureHeader,
  TopFooter,
} from '~/features/common'
import {
  getAllFacilities,
  getAllTypes,
  getAllAreas,
} from '~/models/master.server'
import { searchOffice, SearchOfficeConditions } from '~/models/office.server'
import { Area, Facility, Office, Type } from '~/models/types'

// eslint-disable-next-line @typescript-eslint/naming-convention
export const searchQuerySchema = z
  .object({
    page: z.string().optional(),
    sortOrder: z.string().optional(),
    maxPeople: z.coerce.number().int().min(1).optional(),
  })
  .merge(partialKeywordsSchema)
  .merge(partialAreasMultiSelectFormSchema)
  .merge(partialOfficeTypeMultiSelectFormSchema)
  .merge(partialNumberOfUsersMultiSelectFormSchema)
  .merge(partialFacilitiesSchemaSchema)
  .merge(partialMonthlyCostsFormSchema)

const parseParams = (searchParams: URLSearchParams) => {
  const multiSelectKeys = [
    ...Object.keys(partialAreasMultiSelectFormSchema.shape),
    ...Object.keys(partialOfficeTypeMultiSelectFormSchema.shape),
    ...Object.keys(partialNumberOfUsersMultiSelectFormSchema.shape),
    ...Object.keys(partialFacilitiesSchemaSchema.shape),
  ]

  const transformedParams = Object.fromEntries(
    Array.from(searchParams.entries()).map(([key, value]) => {
      if (multiSelectKeys.includes(key)) {
        return [key, value.includes(',') ? value.split(',') : [value]]
      }

      return [key, value]
    }),
  )

  const parsedSearchParams = searchQuerySchema.safeParse(transformedParams)

  return { parsedSearchParams }
}

const DISPLAY_RESULT_COUNT = 30
const createPageCondition = (
  page: z.infer<typeof searchQuerySchema>['page'],
) => {
  return {
    skip: page ? (Number(page) - 1) * DISPLAY_RESULT_COUNT : 0,
    take: DISPLAY_RESULT_COUNT,
  }
}

const createSortOrderCondition = (
  sortOrder: z.infer<typeof searchQuerySchema>['sortOrder'],
): SearchOfficeConditions['sortOrder'] => {
  switch (sortOrder) {
    case 'registerDateAsc':
      return 'NEWEST'
    case 'monthlyRentAsc':
      return 'CHEAPEST'
    case 'sizeDesc':
      return 'LARGEST'
    case 'updateDateDesc':
      return 'RECENTLY_UPDATED'
    default:
      return undefined
  }
}

const getSearchResults = async (
  searchParams: z.infer<typeof searchQuerySchema>,
) => {
  const searchConditions: SearchOfficeConditions = {
    areaIds: searchParams.areas?.map(Number) ?? [],
    typeIds: searchParams.officeTypes?.map(Number) ?? [],
    facilityIds: searchParams.facilities?.map(Number) ?? [],
    minPeople: Number(searchParams.minPeople) || undefined,
    maxPeople: Number(searchParams.maxPeople) || undefined,
    maxMonthlyRent: Number(searchParams.maxMonthlyRent) || undefined,
    minMonthlyRent: Number(searchParams.minMonthlyRent) || undefined,
    freeWord: searchParams.keyword,

    // ページング、ソート条件
    skip: createPageCondition(searchParams.page).skip,
    take: createPageCondition(searchParams.page).take,
    sortOrder: createSortOrderCondition(searchParams.sortOrder),
  }

  // nullとundefinedはパラメータに含めない
  const filteredParams = Object.fromEntries(
    Object.entries(searchConditions).filter(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ([paramKey, value]) => value != null,
    ),
  )

  return await searchOffice(filteredParams)
}

const PAGE_NUMBER_PARAM_NAME = 'page'
const DEFAULT_PAGE = 1
const SORT_ORDER_PARAM_NAME = 'sortOrder'
const useParams = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const pageParam =
    Number(searchParams.get(PAGE_NUMBER_PARAM_NAME)) || DEFAULT_PAGE
  const changePage = (page: number) => {
    setSearchParams((prev) => {
      prev.set(PAGE_NUMBER_PARAM_NAME, page.toString())
      return prev
    })
  }

  const sortOrderParamString = searchParams.get(SORT_ORDER_PARAM_NAME)
  const sortOrderParam = isSearchResultOrderType(sortOrderParamString)
    ? sortOrderParamString
    : SEARCH_RESULT_ORDERS[0].value

  const changeSortOrder = (sortOrder: SearchResultOrder) => {
    setSearchParams((prev) => {
      prev.set(SORT_ORDER_PARAM_NAME, sortOrder)
      prev.set(PAGE_NUMBER_PARAM_NAME, DEFAULT_PAGE.toString())
      return prev
    })
  }

  return {
    pageParam,
    changePage,
    sortOrderParam,
    changeSortOrder,
  }
}

const transformOfficesForComponent = (
  offices: Office[],
): ComponentProps<typeof SearchResultPage>['offices'] => {
  return offices.map((office) => ({
    id: office.id,
    facilityName: office.name,
    address: office.address,
    buildingName: office.building || '',
    transportationInfo:
      office.stations
        ?.map(
          (station) =>
            `${station.name}駅${station.transportation === 'WALK' ? '徒歩' : 'バス'}${station.time}分`,
        )
        .join('、') || '',
    otherAccess:
      office.otherAccesses
        ?.map(
          (otherAccess) =>
            `（${otherAccess.accessType === 'BUS' ? 'バス' : 'その他'}）${otherAccess.description}`,
        )
        .join('、') || '',
    officeDetailLink: `/prefectures/${office.areas[0]?.prefectures[0]?.slug || 'tokyo'}/offices/${office.id}`,
    contactLink: `/prefectures/${office.areas[0]?.prefectures[0]?.slug || 'tokyo'}/offices/${office.id}/contact`,
    imageSrc:
      (
        office.images
          .sort((a, b) => a.priority - b.priority)
          .find((image) => image.isEyeCatch) || office.images[0]
      )?.url || NO_IMAGE_URL,
    spaces: office.spaces
      .filter((space) => space.availability !== 'UNAVAILABLE')
      .map((space) => ({
        id: space.id,
        types: space.type.map((t) => t.name),
        price: space.monthlyCostExcludingTax,
        priceTaxIncluded: space.monthlyCostIncludingTax,
        pricePrivateFlag: space.isMonthlyCostPrivate,
        capacity: space.maxPeople,
      })),
  }))
}

const createSearchConditionText = (
  searchParams: z.infer<typeof searchQuerySchema>,
  allAreas: Area[],
  allTypes: Type[],
  allFacilities: Facility[],
) => {
  const areaNamesText = allAreas
    .filter((area) => searchParams.areas?.includes(area.id.toString()))
    .map((area) => area.name)
    .join('、')

  const typeNamesText = allTypes
    .filter((type) => searchParams.officeTypes?.includes(type.id.toString()))
    .map((type) => type.name)
    .join('、')

  const facilityNamesText = allFacilities
    .filter((facility) =>
      searchParams.facilities?.includes(facility.id.toString()),
    )
    .map((facility) => facility.name)
    .join('、')

  const maxPeopleText = searchParams.minPeople
    ? `${searchParams.minPeople}名以上`
    : searchParams.maxPeople
      ? `${searchParams.maxPeople}名以下`
      : ''

  const monthlyRentText = searchParams.minMonthlyRent
    ? `${searchParams.minMonthlyRent}円〜`
    : searchParams.maxMonthlyRent
      ? `〜${searchParams.maxMonthlyRent}円`
      : ''

  const searchConditionText =
    [
      areaNamesText,
      typeNamesText,
      facilityNamesText,
      maxPeopleText,
      monthlyRentText,
    ]
      .filter(Boolean)
      .join('、') || '指定なし'

  return { searchConditionText }
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData()
  const submission = parseWithZod(formData, { schema: searchFormSchema })

  if (submission.status !== 'success') {
    return submission.reply()
  }

  const queryParams = new URLSearchParams()

  formData.forEach((value, key) => {
    if (value.toString().trim() !== '') {
      if (queryParams.has(key)) {
        const existingValue = queryParams.get(key)
        queryParams.set(key, `${existingValue},${value.toString()}`)
      } else {
        queryParams.set(key, value.toString())
      }
    }
  })

  const searchUrl = `/new-offices/search?${queryParams.toString()}`

  return redirect(searchUrl)
}

export const loader: LoaderFunction = async ({ request }) => {
  const { searchParams } = new URL(request.url)

  const { parsedSearchParams } = parseParams(searchParams)

  if (!parsedSearchParams.success) {
    throw new Response('Invalid search parameters', { status: 400 })
  }

  const [allAreas, allTypes, allFacilities] = await Promise.all([
    getAllAreas(),
    getAllTypes(),
    getAllFacilities(),
  ])

  const { searchConditionText } = createSearchConditionText(
    parsedSearchParams.data,
    allAreas,
    allTypes,
    allFacilities,
  )

  const { offices, count } = await getSearchResults(parsedSearchParams.data)

  const parsedParamsData = parsedSearchParams.data

  const initialFormValues = {
    areas: parsedParamsData.areas || [],
    officeTypes: parsedParamsData.officeTypes || [],
    facilities: parsedParamsData.facilities || [],
    minPeople: parsedParamsData.minPeople?.toString() || '',
    minMonthlyRent: parsedParamsData.minMonthlyRent?.toString() || '',
    maxMonthlyRent: parsedParamsData.maxMonthlyRent?.toString() || '',
    keyword: parsedParamsData.keyword || '',
  }

  return json({
    searchResultCount: count,
    offices,
    searchConditionText,
    allAreas,
    allTypes,
    allFacilities,
    initialFormValues,
  })
}

export const meta: MetaFunction = () => {
  return [
    {
      title:
        '新着オフィス検索結果 | シェアオフィス コワーキングスペース検索サイト',
    },
  ]
}

export default function Index() {
  const {
    searchResultCount,
    offices,
    searchConditionText,
    allAreas,
    allTypes,
    allFacilities,
    initialFormValues,
  } = useLoaderData<typeof loader>()

  const { pageParam, changePage, sortOrderParam, changeSortOrder } = useParams()

  return (
    <div>
      {/* <TopHeader /> */}
      <PrefectureHeader
        isFromTopPage
        prefecture={{ name: '東京', slug: 'tokyo' }}
      />

      <Container>
        <Breadcrumb
          links={[
            { text: 'トップ', href: '/' },
            { text: '新着オフィス' },
          ]}
        />
      </Container>
      <SearchResultPage
        areas={allAreas}
        changePage={changePage}
        changeSortOrder={changeSortOrder}
        currentPage={pageParam}
        facilities={allFacilities}
        initialFormValues={initialFormValues}
        isFromTopPage
        officeTypes={allTypes}
        offices={transformOfficesForComponent(offices)}
        prefectureName="全国"
        prefectureSlug=""
        searchConditionText={searchConditionText}
        searchResultCount={searchResultCount}
        sortOrder={sortOrderParam}
      />
      <TopFooter />
    </div>
  )
}
